import { usePersistedOpen } from "@/lib/usePersistedOpen";
import { useState, useEffect } from "react";
import { getOverseasMarkets, analyzeOverseasOpportunities } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WoodrowBot from "@/components/WoodrowBot";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Globe, Sparkles, TrendingUp, TrendingDown, Clock, Radio,
  AlertCircle, Target, ChevronRight, ChevronDown, RefreshCw, Zap, ArrowUpCircle, ArrowDownCircle,
  FileText, ExternalLink, Newspaper, BarChart2
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const FLAG_EMOJI = {
  JP: "\u{1F1EF}\u{1F1F5}", CN: "\u{1F1E8}\u{1F1F3}", HK: "\u{1F1ED}\u{1F1F0}",
  KR: "\u{1F1F0}\u{1F1F7}", AU: "\u{1F1E6}\u{1F1FA}", IN: "\u{1F1EE}\u{1F1F3}",
  GB: "\u{1F1EC}\u{1F1E7}", DE: "\u{1F1E9}\u{1F1EA}", FR: "\u{1F1EB}\u{1F1F7}",
  EU: "\u{1F1EA}\u{1F1FA}", CH: "\u{1F1E8}\u{1F1ED}",
};

const LINK_ITEMS = [
  { key: "sec_filings", icon: FileText, label: "SEC Filings" },
  { key: "website", icon: Globe, label: "Website" },
  { key: "news", icon: Newspaper, label: "News" },
  { key: "yahoo_finance", icon: BarChart2, label: "Yahoo Finance" },
  { key: "investor_relations", icon: ExternalLink, label: "Investor Relations" },
];

function StockLinkBar({ links }) {
  if (!links) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-0.5">
        {LINK_ITEMS.map(({ key, icon: Icon, label }) => {
          const url = links[key];
          if (!url) return null;
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-1 text-bt-text-tertiary hover:text-bt-gold transition-colors"
                  data-testid={`link-${key}`}
                >
                  <Icon className="w-3 h-3" strokeWidth={1.5} />
                </a>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary text-xs font-mono px-2 py-1">
                {label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

function StatusDot({ status }) {
  const color = status === "open" ? "bg-bt-positive" : status === "pre-market" ? "bg-bt-gold" : "bg-bt-text-tertiary";
  return (
    <span className="relative flex h-2 w-2">
      {status === "open" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-50" />}
      <span className={`relative inline-flex rounded-full h-2 w-2 ${color}`} />
    </span>
  );
}

function MarketCard({ market }) {
  const isPositive = market.change_pct >= 0;
  return (
    <div className="bg-bt-surface border border-bt-border-subtle px-3 py-2.5 panel-hover transition-all min-w-0" data-testid={`market-card-${market.symbol}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm">{FLAG_EMOJI[market.flag] || ""}</span>
          <p className="font-mono text-xs font-medium text-bt-text-primary truncate">{market.name}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <StatusDot status={market.status} />
          <span className={`text-[10px] font-mono ${
            market.status === "open" ? "text-bt-positive" : market.status === "pre-market" ? "text-bt-gold" : "text-bt-text-tertiary"
          }`}>
            {market.status === "open" ? "LIVE" : market.status === "pre-market" ? "PRE" : "CLSD"}
          </span>
        </div>
      </div>
      <p className="font-mono text-lg font-medium tracking-tighter text-bt-text-primary leading-none">
        {market.value.toLocaleString()}
      </p>
      <div className={`flex items-center gap-1 font-mono text-xs mt-1 ${isPositive ? "text-bt-positive" : "text-bt-negative"}`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {isPositive ? "+" : ""}{market.change_pct}%
        <span className="text-bt-text-tertiary ml-auto font-mono text-[10px]">{market.local_time}</span>
      </div>
    </div>
  );
}

function OpportunityCard({ opp, index }) {
  const isBullish = opp.direction === "Bullish";
  return (
    <div className={`bg-bt-surface border border-bt-border-subtle p-5 panel-hover stagger-${Math.min(index + 1, 6)}`} data-testid={`opportunity-${index}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 flex items-center justify-center ${isBullish ? "bg-bt-positive/10 border border-bt-positive/30" : "bg-bt-negative/10 border border-bt-negative/30"}`}>
            {isBullish ? <TrendingUp className="w-4 h-4 text-bt-positive" /> : <TrendingDown className="w-4 h-4 text-bt-negative" />}
          </div>
          <div>
            <p className="font-body text-sm font-medium text-bt-text-primary">{opp.title}</p>
            <Badge variant="outline" className="text-[10px] font-mono border-bt-border-subtle text-bt-text-tertiary mt-0.5">{opp.region}</Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs font-mono uppercase tracking-wider text-bt-text-tertiary">Confidence</p>
          <p className={`font-mono text-lg font-medium ${opp.confidence >= 70 ? "text-bt-positive" : opp.confidence >= 50 ? "text-bt-gold" : "text-bt-text-secondary"}`}>
            {opp.confidence}%
          </p>
        </div>
      </div>
      <p className="text-sm text-bt-text-secondary font-body leading-relaxed mb-3">{opp.description}</p>
      {opp.affected_us_tickers && opp.affected_us_tickers.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono text-bt-text-tertiary">US Tickers:</span>
          {opp.affected_us_tickers.map(t => (
            <a
              key={t}
              href={`/stock/${t}`}
              className="font-mono text-xs font-medium text-bt-gold hover:text-bt-gold-light transition-colors"
              data-testid={`opp-ticker-${t}`}
            >
              {t} <ChevronRight className="inline w-3 h-3" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WoodrowView() {
  const [markets, setMarkets] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [overseasOpen, setOverseasOpen] = usePersistedOpen("woodrow_overseas", true);

  const fetchMarkets = () => {
    setLoadingMarkets(true);
    getOverseasMarkets()
      .then(r => { setMarkets(r.data); setLoadingMarkets(false); })
      .catch(() => setLoadingMarkets(false));
  };

  useEffect(() => {
    fetchMarkets();
  }, []);

  const handleAnalyze = async () => {
    setLoadingAnalysis(true);
    setAnalysisError(null);
    try {
      const res = await analyzeOverseasOpportunities();
      setAnalysis(res.data?.analysis || null);
    } catch (e) {
      setAnalysisError(e.response?.data?.detail || "Analysis failed");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const openCount = markets
    ? [...(markets.asia || []), ...(markets.europe || [])].filter(m => m.status === "open").length
    : 0;

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" data-testid="woodrow-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 sm:mb-8 gap-3 sm:gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary">
            Woodrow
          </h1>
          <p className="text-sm sm:text-base text-bt-text-secondary font-body mt-1 sm:mt-2">
            AI-powered global opportunity scanner
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-bt-surface border border-bt-border-subtle">
            <Radio className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-bt-positive" strokeWidth={1.5} />
            <span className="font-mono text-[10px] sm:text-xs text-bt-text-secondary">{openCount} markets live</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-bt-surface border border-bt-border-subtle">
            <Globe className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-bt-gold" strokeWidth={1.5} />
            <span className="font-mono text-[10px] sm:text-xs text-bt-text-secondary">
              US Markets: <span className={markets?.us_market_status === "open" ? "text-bt-positive" : "text-bt-text-tertiary"}>
                {markets?.us_market_status === "open" ? "OPEN" : "CLOSED"}
              </span>
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMarkets}
            className="border-bt-border-focus text-bt-text-secondary hover:text-bt-text-primary hover:bg-bt-surface-2"
            data-testid="refresh-markets-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Markets Column */}
        <div className={`${overseasOpen ? "lg:col-span-2" : "lg:col-span-3"} space-y-4`}>
          <Tabs defaultValue="asia">
            <div
              className="flex items-center justify-between mb-3 w-full text-left"
              data-testid="toggle-overseas"
            >
              <button
                onClick={() => setOverseasOpen(!overseasOpen)}
                className="flex items-center gap-2"
              >
                <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary flex items-center gap-2">
                  <Globe className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
                  Overseas Markets
                  <ChevronDown className={`w-4 h-4 text-bt-text-tertiary transition-transform ${overseasOpen ? "rotate-180" : ""}`} />
                </h2>
              </button>
              {overseasOpen && (
              <TabsList className="bg-bt-surface h-8">
                <TabsTrigger value="asia" className="text-xs px-4 h-7 data-[state=active]:bg-bt-gold/20 data-[state=active]:text-bt-gold" data-testid="tab-asia">
                  Asia-Pacific
                </TabsTrigger>
                <TabsTrigger value="europe" className="text-xs px-4 h-7 data-[state=active]:bg-bt-gold/20 data-[state=active]:text-bt-gold" data-testid="tab-europe">
                  Europe
                </TabsTrigger>
              </TabsList>
              )}
            </div>

            {overseasOpen && (
            <>
            <TabsContent value="asia" className="mt-0">
              {loadingMarkets ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-bt-surface border border-bt-border-subtle p-3">
                      <Skeleton className="h-4 w-20 bg-bt-surface-2 mb-2" />
                      <Skeleton className="h-5 w-16 bg-bt-surface-2 mb-1" />
                      <Skeleton className="h-3 w-12 bg-bt-surface-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {(markets?.asia || []).map(m => <MarketCard key={m.symbol} market={m} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="europe" className="mt-0">
              {loadingMarkets ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="bg-bt-surface border border-bt-border-subtle p-3">
                      <Skeleton className="h-4 w-20 bg-bt-surface-2 mb-2" />
                      <Skeleton className="h-5 w-16 bg-bt-surface-2 mb-1" />
                      <Skeleton className="h-3 w-12 bg-bt-surface-2" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {(markets?.europe || []).map(m => <MarketCard key={m.symbol} market={m} />)}
                </div>
              )}
            </TabsContent>
            </>
            )}
          </Tabs>
        </div>

        {/* AI Analysis Column */}
        {overseasOpen && (
        <div className="space-y-4">
          {/* Scan Button */}
          {!analysis && !loadingAnalysis && (
            <div className="ai-glow p-6 animate-pulse-gold" data-testid="woodrow-ai-idle">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-medium text-bt-text-primary">Woodrow AI</h3>
              </div>
              <p className="text-sm text-bt-text-secondary font-body leading-relaxed mb-2">
                Scan overseas markets for cross-border opportunities and overnight signals that could impact US stocks at open.
              </p>
              {analysisError && (
                <div className="flex items-center gap-2 text-bt-negative text-sm mb-3" data-testid="woodrow-ai-error">
                  <AlertCircle className="w-4 h-4" /> {analysisError}
                </div>
              )}
              <Button
                onClick={handleAnalyze}
                className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body font-medium"
                data-testid="woodrow-scan-btn"
              >
                <Zap className="w-4 h-4 mr-2" /> Scan for Opportunities
              </Button>
            </div>
          )}

          {/* Loading */}
          {loadingAnalysis && (
            <div className="ai-glow p-6" data-testid="woodrow-ai-loading">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-5 h-5 text-bt-gold animate-spin" strokeWidth={1.5} />
                <h3 className="font-heading text-xl font-medium text-bt-text-primary">Scanning markets...</h3>
              </div>
              <div className="space-y-3">
                <Skeleton className="h-6 w-40 bg-bt-surface-2" />
                <Skeleton className="h-4 w-full bg-bt-surface-2" />
                <Skeleton className="h-4 w-3/4 bg-bt-surface-2" />
                <Skeleton className="h-20 w-full bg-bt-surface-2" />
                <Skeleton className="h-20 w-full bg-bt-surface-2" />
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {analysis && !loadingAnalysis && (
            <>
              {/* Mood & Headline */}
              <div className="ai-glow p-6" data-testid="woodrow-ai-result">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
                    <h3 className="font-heading text-lg font-medium text-bt-text-primary">Woodrow AI</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleAnalyze}
                    className="text-bt-text-secondary hover:text-bt-gold text-xs font-mono"
                    data-testid="woodrow-rescan-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Rescan
                  </Button>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 font-mono text-sm font-bold ${
                    analysis.market_mood === "Bullish" ? "badge-buy" :
                    analysis.market_mood === "Bearish" ? "badge-sell" : "badge-hold"
                  }`} data-testid="woodrow-mood">
                    {analysis.market_mood === "Bullish" ? <TrendingUp className="w-4 h-4" /> :
                     analysis.market_mood === "Bearish" ? <TrendingDown className="w-4 h-4" /> :
                     <Globe className="w-4 h-4" />}
                    {analysis.market_mood}
                  </div>
                </div>

                {analysis.headline && (
                  <p className="font-heading text-base italic text-bt-text-primary leading-relaxed mb-4" data-testid="woodrow-headline">
                    "{analysis.headline}"
                  </p>
                )}

                <Separator className="bg-bt-border-subtle mb-4" />

                {/* Key Signals */}
                {analysis.key_signals && (
                  <div className="mb-4">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-3">Key Signals</p>
                    <div className="space-y-2">
                      {analysis.key_signals.map((s, i) => (
                        <div key={i} className="flex items-start gap-2" data-testid={`woodrow-signal-${i}`}>
                          <Zap className="w-3.5 h-3.5 text-bt-gold mt-0.5 shrink-0" />
                          <p className="text-sm text-bt-text-secondary font-body">{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Watch for Open */}
                {analysis.watch_for_open && (
                  <div className="bg-bt-surface-2 border border-bt-border-subtle p-4" data-testid="woodrow-watch-open">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-gold mb-2 flex items-center gap-1">
                      <Target className="w-3.5 h-3.5" /> Watch at US Open
                    </p>
                    <p className="text-sm text-bt-text-secondary font-body leading-relaxed">{analysis.watch_for_open}</p>
                  </div>
                )}
              </div>

              {/* Opportunities */}
              {analysis.opportunities && analysis.opportunities.length > 0 && (
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
                    Opportunities ({analysis.opportunities.length})
                  </p>
                  <ScrollArea className="max-h-[600px]">
                    <div className="space-y-3">
                      {analysis.opportunities.map((opp, i) => (
                        <OpportunityCard key={i} opp={opp} index={i} />
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>

      {/* Woodrow Trading Bot - 2 column layout */}
      <div className="">
        <WoodrowBot />
      </div>
    </div>
  );
}
